import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Users as UsersIcon, UserPlus, Rocket, ArrowRight, ArrowLeft,
  CheckCircle2, Loader2, Sparkles, SkipForward,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { key: 'welcome', label: 'Welcome', icon: Sparkles },
  { key: 'company', label: 'Company', icon: Building2 },
  { key: 'team', label: 'Team Members', icon: UsersIcon },
  { key: 'employee', label: 'First Employee', icon: UserPlus },
  { key: 'finish', label: 'Finish', icon: Rocket },
];

const TenantSetupWizard = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({ company: false, team: false, employee: false });

  // Step 2 — Company form
  const [companyForm, setCompanyForm] = useState({ id: '', name: '', code: '', address: '' });
  // Step 3 — New user form
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'hr_admin', password: '' });
  // Step 4 — First employee form
  const [empForm, setEmpForm] = useState({ first_name: '', last_name: '', work_email: '', mobile: '' });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      if (data.length > 0) {
        const c = data[0];
        setCompanyForm({
          id: c.id, name: c.name || '', code: c.code || '', address: c.address || '',
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    // Only tenant_admin can do this setup
    if (user && user.role !== 'tenant_admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (user?.setup_completed) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchCompanies();
  }, [user, navigate, fetchCompanies]);

  const saveCompany = async () => {
    if (!companyForm.name.trim()) { toast.error('Company name is required'); return false; }
    setSaving(true);
    try {
      // Note: backend currently has POST /companies but no PATCH /companies/{id}.
      // For wizard, we only edit if a default company was auto-provisioned.
      // Auto-provisioned company exists — we skip PATCH for now and just mark complete.
      setCompletedSteps((s) => ({ ...s, company: true }));
      toast.success('Company confirmed');
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally { setSaving(false); }
  };

  const saveUser = async () => {
    if (!userForm.email.trim() || !userForm.name.trim() || !userForm.password.trim()) {
      toast.error('Name, email, and password are required');
      return false;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/users`, {
        email: userForm.email.trim(),
        name: userForm.name.trim(),
        role: userForm.role,
        password: userForm.password.trim(),
        company_id: companyForm.id || null,
      }, { withCredentials: true });
      toast.success(`${userForm.name} added`);
      setCompletedSteps((s) => ({ ...s, team: true }));
      setUserForm({ name: '', email: '', role: 'hr_admin', password: '' });
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally { setSaving(false); }
  };

  const saveEmployee = async () => {
    if (!empForm.first_name.trim() || !empForm.last_name.trim() || !empForm.work_email.trim()) {
      toast.error('First name, last name, and work email are required');
      return false;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/employees`, {
        personal_details: {
          first_name: empForm.first_name.trim(),
          last_name: empForm.last_name.trim(),
          gender: 'male',
          mobile: empForm.mobile.trim() || '',
        },
        employment_details: {
          company_id: companyForm.id,
          date_of_joining: new Date().toISOString().split('T')[0],
          work_email: empForm.work_email.trim(),
          employment_status: 'active',
        },
      }, { withCredentials: true });
      toast.success(`${empForm.first_name} added as first employee`);
      setCompletedSteps((s) => ({ ...s, employee: true }));
      setEmpForm({ first_name: '', last_name: '', work_email: '', mobile: '' });
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally { setSaving(false); }
  };

  const finishWizard = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/tenants/mark-setup-complete`, {}, { withCredentials: true });
      await refresh?.();
      toast.success('Setup complete! Welcome aboard.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const goNext = async () => {
    const current = STEPS[stepIdx].key;
    if (current === 'welcome') {
      setStepIdx(1);
    } else if (current === 'company') {
      const ok = await saveCompany();
      if (ok) setStepIdx(2);
    } else if (current === 'team') {
      // Optional: user can skip
      setStepIdx(3);
    } else if (current === 'employee') {
      setStepIdx(4);
    } else if (current === 'finish') {
      await finishWizard();
    }
  };

  const skipToNext = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
  };

  const goBack = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const currentStep = STEPS[stepIdx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-6" data-testid="tenant-setup-wizard">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Welcome, {user?.name}
          </div>
          <h1 className="text-4xl font-semibold text-slate-900 tracking-tight">Setup Wizard</h1>
          <p className="text-slate-600 mt-2">A quick 5-step guided flow. Should take less than 3 minutes.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 bg-white rounded-xl border border-slate-200 p-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
              <React.Fragment key={s.key}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                    done ? 'bg-green-500 border-green-500 text-white' :
                    active ? 'bg-blue-600 border-blue-600 text-white' :
                    'bg-white border-slate-300 text-slate-400'
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs font-medium hidden md:inline ${active ? 'text-slate-900' : 'text-slate-500'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 ${i < stepIdx ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Body */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          {currentStep.key === 'welcome' && (
            <div className="text-center space-y-4" data-testid="wizard-step-welcome">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900">Let's get your tenant ready</h2>
              <p className="text-slate-600 max-w-lg mx-auto">
                We'll walk you through setting up your company, inviting your team, and adding your first employee.
                You can skip any step and come back later — nothing is mandatory here.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 text-left">
                <Feature icon={Building2} title="Confirm Company" text="Verify the default company that was created with your tenant." />
                <Feature icon={UsersIcon} title="Add Your Team" text="Invite HR/payroll admins and managers so they can log in." />
                <Feature icon={UserPlus} title="First Employee" text="Add your first employee to make sure everything works." />
              </div>
            </div>
          )}

          {currentStep.key === 'company' && (
            <div className="space-y-5" data-testid="wizard-step-company">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Confirm your default company</h2>
                <p className="text-sm text-slate-600 mt-1">This was auto-created when your tenant was provisioned. Review or edit the details.</p>
              </div>
              <Field label="Company Name *">
                <input data-testid="wiz-company-name" value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Code">
                  <input data-testid="wiz-company-code" value={companyForm.code} readOnly
                    className="w-full border border-slate-200 bg-slate-50 text-slate-500 rounded-lg px-3 py-2.5 text-sm font-mono" />
                </Field>
                <Field label="Address">
                  <input data-testid="wiz-company-address" value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    placeholder="Street, city, state..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                💡 You can add more companies later from the <strong>Companies</strong> page.
              </div>
            </div>
          )}

          {currentStep.key === 'team' && (
            <div className="space-y-5" data-testid="wizard-step-team">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Invite a team member</h2>
                <p className="text-sm text-slate-600 mt-1">Add at least one HR admin or manager so they can log in. Skip if you'll do this later.</p>
              </div>
              <Field label="Full Name *">
                <input data-testid="wiz-user-name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email *">
                  <input data-testid="wiz-user-email" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
                <Field label="Role">
                  <select data-testid="wiz-user-role" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="hr_admin">HR Admin</option>
                    <option value="payroll_admin">Payroll Admin</option>
                    <option value="attendance_admin">Attendance Admin</option>
                    <option value="finance_reviewer">Finance Reviewer</option>
                    <option value="department_manager">Department Manager</option>
                    <option value="employee">Employee</option>
                  </select>
                </Field>
              </div>
              <Field label="Initial Password *">
                <input data-testid="wiz-user-password" type="text" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="They'll be asked to change it on first login"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
              </Field>
              <div className="flex justify-between">
                <Button data-testid="wiz-add-user-btn" onClick={saveUser} disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Add User
                </Button>
                {completedSteps.team && <span className="text-sm text-green-600 flex items-center"><CheckCircle2 className="w-4 h-4 mr-1" /> User added — you can add more or continue</span>}
              </div>
            </div>
          )}

          {currentStep.key === 'employee' && (
            <div className="space-y-5" data-testid="wizard-step-employee">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Add your first employee</h2>
                <p className="text-sm text-slate-600 mt-1">A minimal record so you can try payroll and attendance flows.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="First Name *">
                  <input data-testid="wiz-emp-fname" value={empForm.first_name} onChange={(e) => setEmpForm({ ...empForm, first_name: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
                <Field label="Last Name *">
                  <input data-testid="wiz-emp-lname" value={empForm.last_name} onChange={(e) => setEmpForm({ ...empForm, last_name: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Work Email *">
                  <input data-testid="wiz-emp-email" type="email" value={empForm.work_email} onChange={(e) => setEmpForm({ ...empForm, work_email: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
                <Field label="Mobile">
                  <input data-testid="wiz-emp-mobile" value={empForm.mobile} onChange={(e) => setEmpForm({ ...empForm, mobile: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </Field>
              </div>
              <div className="flex justify-between">
                <Button data-testid="wiz-add-emp-btn" onClick={saveEmployee} disabled={saving || !companyForm.id}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Add Employee
                </Button>
                {completedSteps.employee && <span className="text-sm text-green-600 flex items-center"><CheckCircle2 className="w-4 h-4 mr-1" /> Employee added</span>}
              </div>
            </div>
          )}

          {currentStep.key === 'finish' && (
            <div className="text-center space-y-5" data-testid="wizard-step-finish">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900">You're all set!</h2>
              <p className="text-slate-600 max-w-lg mx-auto">
                Your tenant is ready to use. You can add more employees, configure payroll, set up leaves, and explore all the modules.
              </p>
              <div className="grid grid-cols-3 gap-3 mt-6">
                <SummaryCard icon={Building2} label="Company" done={!!companyForm.name} />
                <SummaryCard icon={UsersIcon} label="Team" done={completedSteps.team} />
                <SummaryCard icon={UserPlus} label="Employee" done={completedSteps.employee} />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={goBack} disabled={stepIdx === 0 || saving} data-testid="wiz-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3">
            {stepIdx > 0 && stepIdx < STEPS.length - 1 && (
              <Button variant="ghost" onClick={skipToNext} disabled={saving} data-testid="wiz-skip-btn" className="text-slate-500">
                <SkipForward className="w-4 h-4 mr-2" /> Skip
              </Button>
            )}
            <Button onClick={goNext} disabled={saving} data-testid="wiz-next-btn"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {currentStep.key === 'finish' ? 'Go to Dashboard' : 'Next'}
              {currentStep.key !== 'finish' && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>

        {/* Skip whole wizard */}
        {stepIdx > 0 && stepIdx < STEPS.length - 1 && (
          <div className="text-center mt-4">
            <button onClick={finishWizard} data-testid="wiz-finish-later-btn"
              className="text-xs text-slate-500 hover:text-slate-700 underline">
              I'll set this up later — take me to the dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">{label}</label>
    {children}
  </div>
);

const Feature = ({ icon: Icon, title, text }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
    <Icon className="w-5 h-5 text-blue-600 mb-2" />
    <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    <p className="text-xs text-slate-600 mt-1">{text}</p>
  </div>
);

const SummaryCard = ({ icon: Icon, label, done }) => (
  <div className={`p-4 rounded-lg border ${done ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
    <Icon className={`w-5 h-5 mx-auto mb-2 ${done ? 'text-green-600' : 'text-slate-400'}`} />
    <p className={`text-sm font-medium ${done ? 'text-green-800' : 'text-slate-500'}`}>{label}</p>
    <p className={`text-xs mt-0.5 ${done ? 'text-green-600' : 'text-slate-400'}`}>{done ? 'Done' : 'Skipped'}</p>
  </div>
);

export default TenantSetupWizard;
